import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  MenuItem,
  TextField,
  Box,
  ListItemText,
  Fade,
  Card,
} from "@mui/material";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useSnackbar } from "notistack";
import EditableLine from "./EditableLine";

import fetchUrl from "@/lib/fetchUrl";

import HeaderToolbar from "@/components/HeaderToolbar";

const onLoad = ({ machineCenter, startDate, endDate, accessToken }) =>
  fetch(
    fetchUrl(`${import.meta.env.VITE_VTS_API}/v1/employees_per_day`, {
      filter: {
        machine_center: machineCenter,
        after: startDate.toISOString(),
        before: endDate.toISOString(),
      },
      per: 1000,
    }),
    { headers: { authorization: `Bearer ${accessToken}` } },
  )
    .then((r) => {
      if (r.status >= 400) throw new Error("Failed to fetch");
      else return r.json();
    })
    .then(({ data }) => data)
    .catch((e) => {
      throw new Error(e);
    });

const EmployeesPerDay = ({ machineCenterOptions }) => {
  const userInfo = JSON.parse(window.localStorage.getItem("msToken"));
  const { accessToken } = userInfo || {};
  const [startDate, setStartDate] = useState(moment().startOf("month"));
  const [endDate, setEndDate] = useState(
    moment().add(12, "month").startOf("month"),
  );
  const [dateErrors, setDateErrors] = useState({
    startDate: null,
    endDate: null,
  });
  const [machineCenter, setMachineCenter] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const datesAreValid =
    moment(startDate, "strict").isValid() &&
    moment(endDate, "strict").isValid() &&
    !dateErrors.startDate &&
    !dateErrors.endDate;

  const fieldsAreFilled = machineCenter && datesAreValid;

  //Callback to prevent re-render
  const handleLoadData = useCallback(
    (...params) => {
      setLoading(true);
      return onLoad(...params)
        .then((data) => {
          setData(data);
        })
        .catch((e) => {
          enqueueSnackbar(e.toString(), { variant: "error" });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    if (machineCenter && datesAreValid) {
      setData([]);
      setLoading(true);
      handleLoadData({ startDate, endDate, machineCenter, accessToken });
    }
  }, [
    startDate,
    endDate,
    datesAreValid,
    machineCenter,
    handleLoadData,
    accessToken,
  ]);

  const handleCreate = (params) => {
    fetch(fetchUrl(`${import.meta.env.VITE_VTS_API}/v1/employees_per_day`), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ employees_per_day: params }),
    })
      .catch((e) => {
        enqueueSnackbar(e.toString());
      })
      .finally(() => {
        handleLoadData({
          startDate,
          endDate,
          machineCenter,
          accessToken,
        });
      });
  };

  const handleUpdate = (id, params) => {
    fetch(
      fetchUrl(`${import.meta.env.VITE_VTS_API}/v1/employees_per_day/${id}`),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ employees_per_day: params }),
      },
    )
      .catch((e) => {
        enqueueSnackbar(e.toString(), { variant: "error" });
      })
      .finally(() => {
        handleLoadData({
          startDate,
          endDate,
          machineCenter,
          accessToken,
        });
      });
  };

  const dates = [];
  for (
    let i = startDate?.clone();
    datesAreValid && i <= endDate;
    i.add(1, "month")
  ) {
    dates.push(i.clone());
  }

  return (
    <>
      <HeaderToolbar title="Work center load" loading={loading}>
        <Grid container spacing={2} direction="row" alignItems="center">
          <Grid item xs={12} lg={6}>
            <TextField
              label="Machine Center"
              select
              value={machineCenter}
              color="secondary"
              fullWidth
              onChange={(e) => setMachineCenter(e.target.value)}
              size="small"
              required
              inputProps={{
                sx: {
                  minWidth: { sm: undefined, lg: 200 },
                  maxWidth: 200,
                },
              }}
            >
              <MenuItem value="">None</MenuItem>
              {machineCenterOptions.map((option) => (
                <MenuItem value={option[1]} key={option[1]}>
                  {option[1]} - {option[0]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} lg={3}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => setStartDate(date)}
              views={["year", "month"]}
              maxDate={endDate}
              onError={(err) =>
                setDateErrors((prev) => ({
                  ...prev,
                  startDate: err,
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => setEndDate(date)}
              views={["year", "month"]}
              minDate={startDate}
              onError={(err) =>
                setDateErrors((prev) => ({
                  ...prev,
                  endDate: err,
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </Grid>
        </Grid>
      </HeaderToolbar>
      <Box py={2}>
        {!loading && fieldsAreFilled ? (
          <Fade in>
            <TableContainer component={Card}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "30%" }}>Month</TableCell>
                    <TableCell style={{ width: "27.5%" }}>Employees</TableCell>
                    <TableCell style={{ width: "27.5%" }}>Overtime</TableCell>
                    <TableCell style={{ width: "15%" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dates.map((date, i) => {
                    let record = data.find(
                      (e) => e.day === date.format("YYYY-MM-DD"),
                    );
                    return (
                      <EditableLine
                        key={i + "employees_per_day"}
                        record={record}
                        date={date}
                        machineCenter={machineCenter}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Fade>
        ) : (
          <>
            <Fade in>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <ListItemText
                  style={{ textAlign: "center" }}
                  primary={!loading ? "Select a machine center" : "Loading..."}
                  secondary={!loading ? "And a date range" : ""}
                />
              </Box>
            </Fade>
          </>
        )}
      </Box>
    </>
  );
};

EmployeesPerDay.defaultProps = {
  machineCenterOptions: [
    ["COMPACT CELLE", "WE0306"],
    ["DM 125 DF M-UAKSE", "MA0203"],
    ["DM 160 DF M-UAKSE", "MA0204"],
    ["DOOSAN PUMA 5100LY", "MA0202"],
    ["DOOSAN PUMA700 XLY", "MA0201"],
    ["FERMAT WTF 13 R CNC", "MA0211"],
    ["DMG MORI CLX 450", "MA0212"],
    ["HW/CW TIG", "WE0303"],
    ["MANUELL RØRTRÅD", "WE0305"],
    ["METRONOR", "QC0608"],
    ["MONTERING (MEKANISK ARBEID)", "AS0401"],
    ["MÅLEMASKIN", "QC0607"],
    ["POLYSOUDE - TIGER", "WE0307"],
    ["PTA SVEISING", "WE0309"],
    ["PULVERBUE", "WE0308"],
    ["SCHIESS 27 DE", "MA0205"],
    ["TESTING", "AS0402"],
    ["TIG MANUELL", "WE0304"],
    ["TOSHIBA", "MA0206"],
  ],
};

export default EmployeesPerDay;
